/**
 * Student semester view.
 *
 * A semester is a container: whichever one the student picks, they see that
 * semester's timetable, fees and published results — and nothing leaks in from
 * another one. The choice is remembered so every tab in the portal agrees.
 *
 * Powered by:
 *   GET /api/school/portal/student/semesters
 *   GET /api/school/portal/student/semester-view?term_id=...
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Layers, Loader2, CalendarRange, Receipt, Trophy, CheckCircle, MessageSquareQuote, BookOpen } from 'lucide-react';

const KEY = 'student:semester';
const read = () => { try { return localStorage.getItem(KEY) || ''; } catch (_e) { return ''; } };
const write = (v) => { try { v ? localStorage.setItem(KEY, v) : localStorage.removeItem(KEY); } catch (_e) { /* noop */ } };

export const useStudentSemesters = (portalApi, path = '/student/semesters') => {
  const [termId, setTerm] = useState(read);

  const { data } = useQuery({
    queryKey: ['student-semesters', path],
    queryFn: () => portalApi(path).catch(() => ({ years: [] })),
    refetchOnWindowFocus: false,
  });

  const years = useMemo(() => data?.years || [], [data]);
  const flat = useMemo(
    () => years.flatMap((y) => (y.semesters || []).map((s) => ({ ...s, year_name: y.name }))),
    [years],
  );
  const term = useMemo(() => flat.find((s) => s.id === termId) || null, [flat, termId]);

  const setTermId = (v) => { setTerm(v); write(v); };

  useEffect(() => {
    if (!flat.length) return;
    if (!flat.find((s) => s.id === termId)) {
      const next = data?.current?.term_id || flat.find((s) => s.is_current)?.id || flat[0].id;
      setTerm(next); write(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flat, termId, data]);

  return { years, flat, termId, setTermId, term, hasSemesters: flat.length > 0 };
};

export const SemesterPicker = ({ flat, termId, setTermId, testId = 'student-semester-picker' }) => {
  if (!flat?.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-lg border bg-white px-3 py-2 text-xs shadow-sm">
      <Layers className="h-3.5 w-3.5 text-violet-600" />
      <span className="text-slate-500">Semester</span>
      <select
        value={termId || ''}
        onChange={(e) => setTermId(e.target.value)}
        data-testid={testId}
        className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800"
      >
        {flat.map((s) => (
          <option key={s.id} value={s.id}>
            {s.year_name ? `${s.year_name} · ` : ''}{s.name}{s.is_current ? ' · current' : ''}
          </option>
        ))}
      </select>
      {flat.find((s) => s.id === termId)?.status === 'closed' && (
        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600"
               data-testid="student-semester-closed-badge">closed</Badge>
      )}
    </div>
  );
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const Stat = ({ label, value, tone = 'text-slate-800', testId }) => (
  <Card><CardContent className="p-4 text-center">
    <p className={`text-xl font-bold ${tone}`} data-testid={testId}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </CardContent></Card>
);

const MyModules = ({ portalApi, termId }) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['my-modules', termId],
    queryFn: () => portalApi(`/student/modules?term_id=${termId}`).catch(() => null),
    enabled: !!termId,
    retry: false,
  });

  const register = useMutation({
    mutationFn: (offeringId) => portalApi('/student/modules', {
      method: 'POST',
      body: JSON.stringify({ term_id: termId, offering_ids: [offeringId] }),
    }),
    onSuccess: (r) => {
      const refused = r.refused?.[0];
      if (refused) toast.error(`Not registered — ${refused.reasons.join('; ')}`);
      else toast.success(`Registered · ${r.credits} credits this semester`);
      qc.invalidateQueries({ queryKey: ['my-modules', termId] });
    },
    onError: (e) => toast.error(e.message || 'Could not register that'),
  });

  if (!data) {
    return (
      <Card data-testid="my-modules-card">
        <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-violet-600" /> My modules
        </CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" data-testid="my-modules-empty">
            Your registration for this semester hasn't been issued yet. Once admissions
            registers you, your modules and timetable appear here.
          </p>
        </CardContent>
      </Card>
    );
  }
  const available = data.available || {};
  const mine = (data.registered || []).filter((m) => m.status !== 'dropped');

  return (
    <Card data-testid="my-modules-card">
      <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-violet-600" /> My modules ·{' '}
        {available.credits_taken || 0} of {available.max_credits || 0} credits
      </CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        {mine.map((m) => (
          <div key={m.id} className="flex items-center gap-2 text-xs"
               data-testid={`my-module-${m.module_code}`}>
            <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
            <span className="font-mono">{m.module_code}</span>
            <span className="flex-1 truncate">{m.module_name}</span>
            <span className="text-slate-400">{m.credits} cr</span>
          </div>
        ))}
        {!(available.items || []).length ? (
          <p className="text-xs text-slate-500">
            Nothing is on offer for this semester yet.
          </p>
        ) : (available.items || []).map((m) => (
          <div key={m.id} className="flex items-center gap-2 text-xs"
               data-testid={`offer-${m.module_code}`}>
            <span className="font-mono">{m.module_code}</span>
            <span className="flex-1 truncate">{m.module_name}</span>
            <span className="text-slate-400">{m.credits} cr</span>
            {m.eligible ? (
              <button onClick={() => register.mutate(m.id)} disabled={register.isPending}
                      data-testid={`offer-register-${m.module_code}`}
                      className="text-violet-600 hover:underline">Register</button>
            ) : (
              <span className="text-amber-700 max-w-[45%] text-right"
                    data-testid={`offer-blocked-${m.module_code}`}>{m.reasons[0]}</span>
            )}
          </div>
        ))}
        {(data.timetable?.periods || []).length > 0 && (
          <div className="border-t pt-2 space-y-0.5" data-testid="my-module-timetable">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
              From your modules
            </p>
            {data.timetable.periods.map((p, i) => (
              <p key={i} className="text-xs text-slate-600 capitalize">
                {p.day} {p.start}–{p.end} · {p.module_code}
                {p.room ? ` · ${p.room}` : ''}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const StudentSemesterView = ({ portalApi }) => {
  const { flat, termId, setTermId, hasSemesters } = useStudentSemesters(portalApi);

  const { data, isLoading } = useQuery({
    queryKey: ['student-semester-view', termId],
    queryFn: () => portalApi(`/student/semester-view?term_id=${termId}`),
    enabled: !!termId,
  });

  const money = (n) => (Number(n) || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const grouped = useMemo(() => {
    const g = {};
    DAYS.forEach((d) => { g[d] = []; });
    (data?.timetable || []).forEach((s) => {
      const day = s.day_of_week || 'Monday';
      if (g[day]) g[day].push(s);
    });
    Object.values(g).forEach((arr) => arr.sort(
      (a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return g;
  }, [data]);

  if (!hasSemesters) {
    return (
      <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"
            data-testid="student-semester-none">
        Your school has not set up semesters yet.
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="student-semester-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold">My Semester</h2>
        <SemesterPicker flat={flat} termId={termId} setTermId={setTermId}
                        testId="semester-view-picker" />
      </div>

      {isLoading ? (
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      ) : !data ? null : (
        <>
          <div className="rounded-lg border bg-white px-4 py-3 flex items-center gap-2 flex-wrap"
               data-testid="semester-view-header">
            <CalendarRange className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-slate-800">{data.semester.name}</span>
            <span className="text-xs text-slate-500">{data.academic_year.name}</span>
            {data.semester.start_date && (
              <span className="text-xs text-slate-400">
                {data.semester.start_date} → {data.semester.end_date}
              </span>
            )}
            {data.semester.is_current && (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">current</Badge>
            )}
            {data.semester.status === 'closed' && (
              <Badge variant="outline" className="text-[10px]">closed · results final</Badge>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Periods a week" value={(data.timetable || []).length}
                  testId="semester-stat-periods" />
            <Stat label={data.position?.class_name
              ? `Position in ${data.position.class_name}` : 'Position in class'}
                  value={data.position?.class_position
                    ? `${data.position.class_position} of ${data.position.class_size}`
                    : '—'}
                  tone="text-violet-600" testId="semester-stat-position" />
            <Stat label="Billed this semester" value={money(data.fees.total_charges)}
                  testId="semester-stat-billed" />
            <Stat label={data.fees.balance < 0 ? 'Credit on account' : 'Balance due'}
                  value={money(Math.abs(data.fees.balance))}
                  tone={data.fees.balance > 0 ? 'text-red-600' : 'text-emerald-600'}
                  testId="semester-stat-balance" />
            <Stat label="Attendance" value={`${data.attendance.rate}%`}
                  tone="text-indigo-600" testId="semester-stat-attendance" />
          </div>

          {data.position?.overall_position && (
            <p className="text-xs text-slate-500" data-testid="semester-position-line">
              {data.position.class_position} of {data.position.class_size} in
              {' '}{data.position.class_name || 'the class'} · {data.position.overall_position} of
              {' '}{data.position.cohort_size} in the semester · average
              {' '}{data.position.average_percent}%
            </p>
          )}

          {(data.comments?.class_teacher || data.comments?.head_teacher            || data.comments?.conduct || data.comments?.effort) && (
            <Card data-testid="semester-comments-card">
              <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
                <MessageSquareQuote className="h-4 w-4 text-violet-600" /> What your teachers said
              </CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(data.comments.conduct || data.comments.effort) && (
                  <div className="flex gap-2 flex-wrap" data-testid="semester-comment-ratings">
                    {data.comments.conduct && (
                      <Badge variant="outline" className="capitalize">
                        Conduct: {data.comments.conduct.replace('_', ' ')}
                      </Badge>
                    )}
                    {data.comments.effort && (
                      <Badge variant="outline" className="capitalize">
                        Effort: {data.comments.effort.replace('_', ' ')}
                      </Badge>
                    )}
                  </div>
                )}
                {data.comments.class_teacher && (
                  <div data-testid="semester-comment-class-teacher">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Class teacher
                    </p>
                    <p className="text-slate-700 whitespace-pre-line">{data.comments.class_teacher}</p>
                  </div>
                )}
                {data.comments.head_teacher && (
                  <div data-testid="semester-comment-head-teacher">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Head teacher
                    </p>
                    <p className="text-slate-700 whitespace-pre-line">{data.comments.head_teacher}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <MyModules portalApi={portalApi} termId={termId} />

          <Card data-testid="semester-timetable-card">
            <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-indigo-600" /> Timetable
            </CardTitle></CardHeader>
            <CardContent>
              {!(data.timetable || []).length ? (
                <p className="text-sm text-slate-400 py-4 text-center"
                   data-testid="semester-timetable-empty">
                  No timetable published for this semester yet.
                </p>
              ) : (
                <div className="grid gap-2">
                  {DAYS.filter((d) => grouped[d].length).map((day) => (
                    <div key={day}>
                      <p className="text-xs font-semibold text-slate-500 mb-1">{day}</p>
                      <div className="space-y-1.5">
                        {grouped[day].map((s, i) => (
                          <div key={s.id || i}
                               className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="font-medium text-sm">{s.subject_name || 'Subject'}</p>
                              <p className="text-xs text-slate-500">
                                {s.teacher_name || '—'}{s.room ? ` · ${s.room}` : ''}
                              </p>
                            </div>
                            <span className="text-xs text-slate-600">
                              {s.start_time || ''}{s.end_time ? ` – ${s.end_time}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="semester-fees-card">
            <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-4 w-4 text-amber-600" /> Fees
            </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {data.fees.carried_forward > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                   data-testid="semester-fees-carried">
                  {money(data.fees.carried_forward)} of this bill was carried forward from an
                  earlier semester.
                </p>
              )}
              {!(data.fees.charges || []).length ? (
                <p className="text-sm text-slate-400 py-4 text-center"
                   data-testid="semester-fees-empty">
                  Nothing billed to you in this semester.
                </p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Fee</TableHead><TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.fees.charges.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">
                          {c.fee_type_name || c.description || 'Fee'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{c.status || 'pending'}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{money(c.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {(data.fees.payments || []).length > 0 && (
                <div className="text-xs text-emerald-700" data-testid="semester-fees-paid-line">
                  {data.fees.payments.length} payment(s) · {money(data.fees.total_payments)} received
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="semester-results-card">
            <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-emerald-600" /> Results
            </CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!(data.results || []).length ? (
                <p className="text-sm text-slate-400 py-4 text-center"
                   data-testid="semester-results-empty">
                  No published results for this semester yet.
                </p>
              ) : data.results.map((exam) => (
                <div key={exam.exam_id} className="rounded-lg border p-3"
                     data-testid={`semester-result-${exam.exam_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sm">{exam.exam_name}</p>
                      <p className="text-xs text-slate-500 capitalize">{exam.exam_type}</p>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      {exam.percentage}%
                    </Badge>
                  </div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Subject</TableHead><TableHead>Marks</TableHead>
                      <TableHead>Grade</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {exam.subjects.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{s.subject_name}</TableCell>
                          <TableCell>{s.marks} / {s.max_marks}</TableCell>
                          <TableCell>{s.grade || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </CardContent>
          </Card>

          <p className="text-xs text-slate-400 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" />
            Switching semesters never changes or hides anything — every semester keeps its own
            timetable, fees and results.
          </p>
        </>
      )}
    </div>
  );
};

export default StudentSemesterView;
